import React, { FC } from 'react';
import styled from 'styled-components';

interface RetouchingCartPose {
  id?: number;
  url?: string;
  selected_crop?: { url?: string } | null;
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 8px;
`;

// With notes, each photo gets its own row so its note sits beside it.
const NotedList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0 0 8px;
  list-style: none;
`;

const NotedRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const NoteText = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #333;
  overflow-wrap: anywhere;
`;

const Caption = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #666;
`;

export const RETOUCHING_ORIGINALS_CAPTION =
  'Retouching is applied to the original photo and carries through to every print and crop of it.';

const Thumbnail = styled.div`
  flex: none;
  width: 64px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

interface RetouchingCartPhotosProps {
  poses: (RetouchingCartPose | null | undefined)[];
  // The line's per-photo notes, keyed by pose id.
  notes?: Record<string, string> | null;
  frictionPropsFor: (
    pose: RetouchingCartPose,
    url: string | undefined,
  ) => Record<string, unknown>;
}

// A retouching line can cover many photos; a single row of small thumbnails
// keeps it from stretching the cart the way one full-size tile per photo did.
// Each is the original photo, never a print's crop: retouching is applied to
// the original.
const RetouchingCartPhotos: FC<RetouchingCartPhotosProps> = ({
  poses,
  notes,
  frictionPropsFor,
}) => {
  const noteFor = (pose: RetouchingCartPose) =>
    pose.id != null ? notes?.[String(pose.id)]?.trim() : undefined;

  if (poses.some((pose) => pose && noteFor(pose))) {
    return (
      <>
        <NotedList data-testid="retouching-cart-photos">
          {poses.map((pose, index) => {
            if (!pose) return null;
            const url = pose.url;
            const note = noteFor(pose);

            return (
              <NotedRow key={`retouching-photo-${pose.id ?? index}`}>
                <Thumbnail
                  role="img"
                  aria-label={`Photo ${index + 1}`}
                  {...frictionPropsFor(pose, url)}
                  style={{ backgroundImage: `url("${url}")` }}
                />
                {note && <NoteText>{note}</NoteText>}
              </NotedRow>
            );
          })}
        </NotedList>
        <Caption>{RETOUCHING_ORIGINALS_CAPTION}</Caption>
      </>
    );
  }

  return (
    <>
      <Row data-testid="retouching-cart-photos">
        {poses.map((pose, index) => {
          if (!pose) return null;
          const url = pose.url;

          return (
            <Thumbnail
              key={`retouching-photo-${pose.id ?? index}`}
              role="img"
              aria-label={`Photo ${index + 1}`}
              {...frictionPropsFor(pose, url)}
              style={{ backgroundImage: `url("${url}")` }}
            />
          );
        })}
      </Row>
      <Caption>{RETOUCHING_ORIGINALS_CAPTION}</Caption>
    </>
  );
};

export default RetouchingCartPhotos;
